import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LicenseProvider } from "./context/LicenseContext";

// Pages
import LoginPage from "./pages/LoginPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import VoiceSettingsPage from "./pages/VoiceSettingsPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import TeachersPage from "./pages/admin/TeachersPage";
import ClassesPage from "./pages/admin/ClassesPage";
import BrandingPage from "./pages/admin/BrandingPage";
import SettingsPage from "./pages/admin/SettingsPage";
import LicensePage from "./pages/admin/LicensePage";

// Teacher Pages
import TeacherDashboard from "./pages/teacher/TeacherDashboard";
import StudentsPage from "./pages/teacher/StudentsPage";
import TeacherClassesPage from "./pages/teacher/TeacherClassesPage";
import TTSModulePage from "./pages/teacher/TTSModulePage";
import STTModulePage from "./pages/teacher/STTModulePage";
import MCQModulePage from "./pages/teacher/MCQModulePage";
import VMCQModulePage from "./pages/teacher/VMCQModulePage";
import FTBModulePage from "./pages/teacher/FTBModulePage";
import TestModulesPage from "./pages/teacher/TestModulesPage";
import VideoLecturesPage from "./pages/teacher/VideoLecturesPage";
import BackupRestorePage from "./pages/teacher/BackupRestorePage";
import ResourcesPage from "./pages/ResourcesPage";
import StudentVMCQPage from "./pages/student/StudentVMCQPage";
import StudentVideoLecturesPage from "./pages/student/StudentVideoLecturesPage";

// Student Pages
import StudentDashboard from "./pages/student/StudentDashboard";
import StudentTTSPage from "./pages/student/StudentTTSPage";
import StudentSTTPage from "./pages/student/StudentSTTPage";
import StudentMCQPage from "./pages/student/StudentMCQPage";
import StudentFTBPage from "./pages/student/StudentFTBPage";
import LeaderboardPage from "./pages/student/LeaderboardPage";
import StudentProfilePage from "./pages/student/StudentProfilePage";
import StudentFunLearningPage from "./pages/student/StudentFunLearningPage";

// Fun Learning Pages
import TeacherFunLearningPage from "./pages/teacher/TeacherFunLearningPage";

// Teacher Profile Page
import TeacherProfilePage from "./pages/teacher/TeacherProfilePage";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center premium-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/admin" replace />;
      case 'teacher':
        return <Navigate to="/teacher" replace />;
      case 'student':
        return <Navigate to="/student" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  return children;
};

// Home redirect based on role
const HomeRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center premium-bg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  switch (user?.role) {
    case 'admin':
      return <Navigate to="/admin" replace />;
    case 'teacher':
      return <Navigate to="/teacher" replace />;
    case 'student':
      return <Navigate to="/student" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

// License Gate removed - licensing feature disabled
// All routes are now accessible without license checks

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes - Login always accessible for admin activation */}
      <Route path="/login" element={<LoginPage />} />
      
      {/* Home Redirect */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/teachers" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <TeachersPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/classes" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <ClassesPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/branding" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <BrandingPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/voice-settings" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <VoiceSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/admin/license" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <LicensePage />
        </ProtectedRoute>
      } />

      {/* Teacher Routes */}
      <Route path="/teacher" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <TeacherDashboard />
        </ProtectedRoute>
      } />
      <Route path="/teacher/analytics" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <AnalyticsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/students" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <StudentsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/classes" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <TeacherClassesPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/my-classes/:classId" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <TeacherClassesPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/modules" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <TestModulesPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/tts" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <TTSModulePage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/stt" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <STTModulePage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/mcq" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <MCQModulePage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/vmcq" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <VMCQModulePage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/ftb" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <FTBModulePage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/video-lectures" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <VideoLecturesPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/backup-restore" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <BackupRestorePage />
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <ResourcesPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/voice-settings" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <VoiceSettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/fun-learning" element={
        <ProtectedRoute allowedRoles={['teacher', 'admin']}>
          <TeacherFunLearningPage />
        </ProtectedRoute>
      } />
      <Route path="/teacher/profile" element={
        <ProtectedRoute allowedRoles={['teacher']}>
          <TeacherProfilePage />
        </ProtectedRoute>
      } />

      {/* Student Routes */}
      <Route path="/student" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/student/tts" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentTTSPage />
        </ProtectedRoute>
      } />
      <Route path="/student/stt" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentSTTPage />
        </ProtectedRoute>
      } />
      <Route path="/student/mcq" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentMCQPage />
        </ProtectedRoute>
      } />
      <Route path="/student/ftb" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentFTBPage />
        </ProtectedRoute>
      } />
      <Route path="/student/vmcq" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentVMCQPage />
        </ProtectedRoute>
      } />
      <Route path="/student/video-lectures" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentVideoLecturesPage />
        </ProtectedRoute>
      } />

      <Route path="/student/leaderboard" element={
        <ProtectedRoute allowedRoles={['student']}>
          <LeaderboardPage />
        </ProtectedRoute>
      } />
      <Route path="/student/profile" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/student/fun-learning" element={
        <ProtectedRoute allowedRoles={['student']}>
          <StudentFunLearningPage />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LicenseProvider>
          <AuthProvider>
            <AppRoutes />
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                duration: 3000,
              }}
            />
          </AuthProvider>
        </LicenseProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
